// src/services/admin/admin-supportService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const adminSupportApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin/support`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000
});

// Use the admin token here
adminSupportApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor — kick back to login on 401
adminSupportApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminSupportService = {
  /**
   * List contact messages (filters + pagination)
   * @param {Object} params - { page, limit, status, subject, search, sort, dateFrom, dateTo }
   */
  async getMessages(params = {}) {
    const res = await adminSupportApi.get('/', { params });
    return res.data;
  },

  /**
   * Support dashboard stats
   */
  async getStats() {
    const res = await adminSupportApi.get('/stats');
    return res.data;
  },

  /**
   * Single message detail
   */
  async getMessageById(id) {
    const res = await adminSupportApi.get(`/${id}`);
    return res.data;
  },

  /**
   * Update status / priority / adminNotes / assignedTo
   */
  async updateMessage(id, payload = {}) {
    const res = await adminSupportApi.patch(`/${id}`, payload);
    return res.data;
  },

  /**
   * Send a reply email to the submitter
   */
  async replyToMessage(id, body) {
    const res = await adminSupportApi.post(`/${id}/reply`, { body });
    return res.data;
  },

  /**
   * Delete a message
   */
  async deleteMessage(id) {
    const res = await adminSupportApi.delete(`/${id}`);
    return res.data;
  },

  /* ───────── UI helpers ───────── */

  getStatusBadgeConfig(status) {
    const configs = {
      new:        { bgColor: 'bg-[#F4E7E7]', textColor: 'text-[#A82121]', dotColor: 'bg-[#A82121]', label: 'New' },
      in_review:  { bgColor: 'bg-[#FFF4D6]', textColor: 'text-[#D8A100]', dotColor: 'bg-[#D8A100]', label: 'In Review' },
      replied:    { bgColor: 'bg-[#E7ECF4]', textColor: 'text-[#2F578F]', dotColor: 'bg-[#2F578F]', label: 'Replied' },
      resolved:   { bgColor: 'bg-[#E7F4ED]', textColor: 'text-[#2F8F5B]', dotColor: 'bg-[#2F8F5B]', label: 'Resolved' },
      closed:     { bgColor: 'bg-gray-100',  textColor: 'text-gray-700',  dotColor: 'bg-gray-500',  label: 'Closed' },
      spam:       { bgColor: 'bg-gray-100',  textColor: 'text-gray-500',  dotColor: 'bg-gray-400',  label: 'Spam' }
    };
    return configs[status] || configs.new;
  },

  getPriorityBadgeConfig(priority) {
    const configs = {
      low:    { bgColor: 'bg-gray-100',    textColor: 'text-gray-600' },
      normal: { bgColor: 'bg-[#E7ECF4]',   textColor: 'text-[#2F578F]' },
      high:   { bgColor: 'bg-[#FFF4D6]',   textColor: 'text-[#D8A100]' },
      urgent: { bgColor: 'bg-[#F4E7E7]',   textColor: 'text-[#A82121]' }
    };
    return configs[priority] || configs.normal;
  },

  getSubjectLabel(subject) {
    const labels = {
      general:   'General Inquiry',
      support:   'Technical Support',
      billing:   'Billing Question',
      feedback:  'Feedback',
      complaint: 'Complaint',
      other:     'Other'
    };
    return labels[subject] || subject || '—';
  },

  formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  formatDateTime(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  timeAgo(dateString) {
    if (!dateString) return '';
    const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return this.formatDate(dateString);
  }
};

export default adminSupportService;